import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const INTERACTIVE = "a, button, [role='button'], input, textarea, select, label[for], summary, .cursor-pointer";

export const CustomCursor = () => {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, { stiffness: 350, damping: 28, mass: 0.4 });
  const ringY = useSpring(y, { stiffness: 350, damping: 28, mass: 0.4 });

  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only enable on precise-pointer (desktop) devices
    if (typeof window === "undefined") return;
    const coarse = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    if (coarse) return;
    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
    };
    const over = (e) => {
      const target = e.target.closest ? e.target.closest(INTERACTIVE) : null;
      setHovering(!!target);
    };
    const down = () => setPressed(true);
    const up = () => setPressed(false);
    const leave = () => setVisible(false);
    const enter = () => setVisible(true);

    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    document.addEventListener("mouseleave", leave);
    document.addEventListener("mouseenter", enter);

    return () => {
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      document.removeEventListener("mouseleave", leave);
      document.removeEventListener("mouseenter", enter);
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <div data-testid="custom-cursor" aria-hidden="true" style={{ opacity: visible ? 1 : 0 }}>
      {/* Trailing ring */}
      <motion.div
        className="fixed top-0 left-0 z-[9999] pointer-events-none rounded-full border"
        style={{ x: ringX, y: ringY }}
      >
        <motion.div
          className="rounded-full border-2"
          animate={{
            width: hovering ? 56 : 34,
            height: hovering ? 56 : 34,
            marginLeft: hovering ? -28 : -17,
            marginTop: hovering ? -28 : -17,
            borderColor: hovering ? "#C5A059" : "rgba(11,27,61,0.45)",
            backgroundColor: hovering ? "rgba(197,160,89,0.12)" : "rgba(197,160,89,0)",
            scale: pressed ? 0.8 : 1,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
        />
      </motion.div>

      {/* Precise dot */}
      <motion.div
        className="fixed top-0 left-0 z-[9999] pointer-events-none"
        style={{ x, y }}
      >
        <motion.div
          className="rounded-full bg-gold"
          animate={{
            width: hovering ? 6 : 8,
            height: hovering ? 6 : 8,
            marginLeft: hovering ? -3 : -4,
            marginTop: hovering ? -3 : -4,
            scale: pressed ? 1.6 : 1,
          }}
          transition={{ type: "spring", stiffness: 500, damping: 24 }}
        />
      </motion.div>
    </div>
  );
};
